import * as React from 'react';
import { Redirect, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as AccessControlSelectors from '../selectors/AccessControlSelectors';
import { getUserAccessRoleId, selectIsAgencyUser } from 'client/js/redux/domains/User/User.selectors';
import NoOrgView from '../../common/screens/NoAccessScreens/NoOrgView';
import NoAgencyView from '../../common/screens/NoAccessScreens/NoAgencyView';
import ForbiddenView from '../../common/screens/NoAccessScreens/ForbiddenView';
import * as RouterService from './RouterServices';
import * as OrgSelectors from '../selectors/OrgSelectors';
import type { AccessResource, PermissionLevel } from '../../../../shared/types';
import type { RootState } from 'client/js/redux';

type Access = {
  permission: PermissionLevel;
  accessResources: AccessResource[];
};

/**
 * This RouteProps intentionally mimic Route API from latest react-router.
 * However, I am leaving out a few props, intentionally, because I would like us
 * not to use them.
 *
 * @link https://reacttraining.com/react-router/web/api/Route
 */
type RouteProps = {
  path: string | string[];
  match?: any;
  strict?: boolean;
  exact?: boolean;
  sensitive?: boolean;
  // Setting this as any for now
  // But really this is React.ComponentType<RouteComponentProps>
  component?: any;
  render?: any;
};

type AccessProps = {
  access?: Access;

  /**
   * redirectTo represent redirection in case, if the route is not allowed
   */
  redirectTo?: string;

  /**
   * We may have to revisit this additionalViews logic.
   * For now, I am going to keep it here.
   */
  additionalViews?: string[];
};

type ProductProps = {
  enabled: boolean;
  productRedirect?: string | null | undefined;
};

type ExternalProps = RouteProps & AccessProps & { product?: ProductProps };

type Props = ExternalProps & {
  allowed?: boolean;
  loading?: boolean;
  accessRoleId?: string;
  isUserOnAgencyView?: boolean;
  isUserOnOrgView?: boolean;
  isAgencyUser?: boolean;
  location?: any;
};
export function ProtectedRoute(props: Props) {
  if (props.loading) {
    return null;
  }

  const { screenAvailableForOrg, screenAvailableForAgency } = RouterService.additionalViews(props.additionalViews);

  if (props.isUserOnAgencyView && !screenAvailableForAgency) {
    return <NoAgencyView />;
  }

  if (props.isUserOnOrgView && !screenAvailableForOrg) {
    return <NoOrgView />;
  }

  if (props.product?.enabled === false) {
    if (!props.product.productRedirect) return null;

    return <Redirect to={props.product.productRedirect} />;
  }

  if (!props.allowed || RouterService.isRouteRestricted(props.isAgencyUser, props.location)) {
    if (props.redirectTo) {
      return <Redirect to={props.redirectTo} />;
    }

    return (
      // @ts-ignore
      <ForbiddenView
        accessResources={props.access && props.access.accessResources}
        role={props.accessRoleId as any}
        loading={props.loading}
      />
    );
  }

  return (
    <Route
      exact={props.exact}
      strict={props.strict}
      sensitive={props.sensitive}
      path={props.path}
      component={props.component}
      render={props.render || undefined} // undefined is important
      match={props.match}
    />
  );
}

const mapStateToProps = (state: RootState, props: ExternalProps) => {
  return {
    loading: AccessControlSelectors.isAccessControlLoading(state),
    allowed: AccessControlSelectors.isAuthorized(state, props.access),
    accessRoleId: getUserAccessRoleId(state),
    isUserOnAgencyView: OrgSelectors.isUserOnAgencyView(state),
    isUserOnOrgView: OrgSelectors.isUserOnOrgView(state),
    isAgencyUser: selectIsAgencyUser(state),
  };
};

const ConnectedProtectedRoute: React.ComponentType<ExternalProps> = connect(
  mapStateToProps,
  null
)(withRouter(ProtectedRoute));
export default ConnectedProtectedRoute;
